import { Decimal, Logger, useIntl } from '@capsicum/common';
import { useConversionRatesQuery, useMainCurrenciesQuery, useNCWBalance } from '@capsicum/socios-core';
import { type PlatformTransactionExecutionParams, type UsePlatformTransactionReturn } from '@socios-blk/core';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import { v2MigrationTxHashAtom } from '../../data-access';
import { type MigrateableFanToken } from '../../models';
import { useMigrationCounterMutation } from '../useMigrationCounterMutation';
import { useV2FTMigrationAnalytics } from '../useV2FTMigrationAnalytics';

type UseV2FTMigrationUpgradeScreen = {
	gasFeeEstimate: UsePlatformTransactionReturn['gasFeeEstimate'];
	tokens: Array<MigrateableFanToken>;
};

export const useV2FTMigrationUpgradeScreen = ({ gasFeeEstimate, tokens }: UseV2FTMigrationUpgradeScreen) => {
	const { $t } = useIntl();
	const [hasErrored, setErrored] = useState(false);
	const setV2MigrationTxHashAtom = useSetAtom(v2MigrationTxHashAtom);
	const [isGasEstimateLoading, setGasEstimateLoading] = useState(false);
	const [isTransactionLoading, setTransactionLoading] = useState(false);
	const { isPending: isNCWBalanceLoading, ncwBalance } = useNCWBalance();
	const { data: currencies, isLoading: isCurrenciesLoading } = useMainCurrenciesQuery();
	const { isPending: isCounterMutationLoading, mutateAsync: mutateMigrationCounter } = useMigrationCounterMutation();

	const { triggerConfirmUpgradeFTMigrationEvent } = useV2FTMigrationAnalytics({ tokens });

	const paymasterParams = useMemo<PlatformTransactionExecutionParams['paymasterParams']>(
		() => ({
			isPaymasterConfigurationEnabled: true,
			paymasterWebhookSecretParams: {
				parameters: [],
				target: 'ft_v2_upgrade_swap'
			}
		}),
		[]
	);

	const { altCurrency, defaultCurrency } = currencies ?? {};

	const { data: rates, isLoading: isConversionRatesLoading } = useConversionRatesQuery(
		!defaultCurrency || !altCurrency ? [] : [{ from: defaultCurrency.id, to: altCurrency.id }]
	);

	const syncMigrationTXHash = useCallback(
		async (txHash: string) => {
			try {
				setV2MigrationTxHashAtom(txHash);
				await mutateMigrationCounter({ hashes: [txHash] });
				setV2MigrationTxHashAtom(null);
			} catch (e) {
				Logger.error('V2Migration: ERROR Failed to record migration tx hash during migration', { e, txHash });
			}
		},
		[mutateMigrationCounter, setV2MigrationTxHashAtom]
	);

	const rate = rates?.[0]?.rate;

	const canEstimateGasFees = !hasErrored && !gasFeeEstimate && !isNCWBalanceLoading && !isGasEstimateLoading;

	const isNetworkFeeLoading =
		isNCWBalanceLoading || isCurrenciesLoading || isGasEstimateLoading || isConversionRatesLoading;

	const isLoading = isNCWBalanceLoading || isNetworkFeeLoading || isTransactionLoading || isCounterMutationLoading;

	return {
		altCurrencyFee:
			!rate || !altCurrency || !gasFeeEstimate || isNetworkFeeLoading
				? ''
				: $t('v2FTMigrationInitFlow.upgrade.sections.networkFee.amount', {
						amount: new Decimal(rate).mul(gasFeeEstimate).toFixed(2),
						currency: altCurrency?.shortCode
					}),
		canEstimateGasFees,
		defaultCurrencyFee:
			!defaultCurrency || !gasFeeEstimate || isNetworkFeeLoading
				? ''
				: $t('v2FTMigrationInitFlow.upgrade.sections.networkFee.amount', {
						amount: gasFeeEstimate.toFixed(2),
						currency: defaultCurrency?.shortCode
					}),
		hasErrored,
		isLoading,
		isNetworkFeeLoading,
		paymasterParams,
		sendAnalytics: triggerConfirmUpgradeFTMigrationEvent,
		setErrored,
		setGasEstimateLoading,
		setTransactionLoading,
		stakingSmartContractAddress: ncwBalance?.stakingSmartContractAddress,

		swapSmartContractAddress: ncwBalance?.v2FTSwapContractAddress,

		syncMigrationTXHash
	};
};
